import { createUseStyles } from "react-jss";
import { colors } from "../../styles/colors";
import { down } from "../../styles/breakpoints";

type Props = {
  isSearchable?: boolean;
  showPagination?: boolean;
  isBackgroundUnique?: boolean;
  isTitleHeader?: boolean;
};

const useStyles = createUseStyles((_) => ({
  container: {
    borderRadius: "15px",
    padding: "20px",
    backgroundColor: colors.WHITE,
    position: 'relative',
    overflow: 'visible', // Changed from hidden to visible
    zIndex: 0, // Base z-index for the container
    // Flex column so the scrollable table area flexes and the pagination
    // footer stays pinned at the bottom of the card (always reachable).
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    // `minWidth: 0` matters as much as `maxWidth`: without it a wide table
    // stretches this flex item past its parent instead of scrolling inside.
    minWidth: 0,
    maxWidth: "100%",

    [down.sm]: {
      // 20px each side is a lot of a 320px screen; the table below is the
      // content that actually needs the room.
      padding: "14px 12px",
      borderRadius: "12px",
    },

    "& .MuiTableContainer-root .MuiTable-root": {
      "& .MuiTableHead-root": {
        "& .MuiTableCell-root": {
          height: "40px",
          padding: "0px 10px",
          textTransform: "uppercase",
          textWrap: "nowrap",
          // The header is sticky (`<Table stickyHeader>`); without an opaque
          // background the striped rows showed through it while scrolling.
          backgroundColor: colors.WHITE,
        },
      },
      "& .MuiTableBody-root": {
        "& .MuiTableCell-root": {
          height: "40px",
          padding: "0px 10px",
          border: 0,
          textWrap: "nowrap",
        },
        "& .MuiTableRow-root:nth-child(even)": {
          backgroundColor: (props: Props) =>
            props.isBackgroundUnique ? colors.WHITE : "#F9F9F9",
        },
      },
    },
  },
  
  formControl: {
    "&.MuiFormControl-root": {
      position: 'relative',
      zIndex: 10, // Higher z-index for search field
      
      "& input": {
        padding: "8px",
        paddingLeft: "16px",
        "&::placeholder": {
          color: colors.BLACK,
          fontFamily: "Poppins-Bold",
          fontSize: "10px",
        },
      },
      "& fieldset": {
        border: "none",
      },
      "& .MuiInputBase-root.MuiOutlinedInput-root": {
        fontFamily: "Poppins-Regular",
        backgroundColor: colors.default,
        borderRadius: "50px",
        fontSize: "11px",
      },
    },
  },
  
  contratTitle: {
    color: colors.primary,
  },
  
  /*
   * The header holds a title, an optional action button, a search field and
   * an icon button on one non-wrapping line. Below roughly 900px they ran
   * into each other and the search box was clipped. It now wraps.
   */
  headerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: (props: Props) =>
      props.isTitleHeader ? "space-between" : "flex-end",
    flexWrap: "wrap",
    gap: "10px",
    marginBottom: "9px",
    position: 'relative',
    zIndex: 10, // Higher z-index for header controls
    [down.sm]: {
      alignItems: "stretch",
    },
  },

  /*
   * The search box was `width: 27ch` inline, which cannot shrink. A class
   * lets it cap at the available width instead of forcing an overflow.
   */
  searchControl: {
    width: "27ch",
    maxWidth: "100%",
    [down.sm]: {
      width: "100%",
    },
  },
  
  footerContainer: {
    marginTop: "13px",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    flexShrink: 0, // Never let the footer be scrolled away.
    flexDirection: (props: Props) =>
      props.showPagination ? "row-reverse" : "row",
    justifyContent: "space-between",
    position: 'relative',
    zIndex: 1,

    /*
     * MUI lays the pagination toolbar out on a single non-wrapping row:
     * "Lignes par page" + selector + "1-10 sur 42" + two arrows. That needs
     * roughly 420px. `footerContainer` above wraps, but the toolbar is a
     * flex item that refused to shrink below its content, so on any phone it
     * pushed this card wider than the viewport and put a horizontal
     * scrollbar under every list in the app -- Departments, Brands, Pub,
     * Users, Companies, all of which render through this component.
     *
     * Fixed here rather than per page: the footer is owned by CustomTable,
     * so every caller inherited the defect and every caller now inherits the
     * fix.
     */
    "& .MuiTablePagination-root": {
      minWidth: 0,
      overflow: "hidden",
    },
    "& .MuiTablePagination-toolbar": {
      flexWrap: "wrap",
      justifyContent: "flex-end",
      rowGap: "4px",
      minHeight: "auto",
      paddingLeft: 0,
    },
    "& .MuiTablePagination-displayedRows": {
      margin: 0,
    },
    "& .MuiTablePagination-selectLabel": {
      margin: 0,
      /* The selector immediately to its right already says what it is, so
         the label is the first thing that can go when space is short. */
      [down.sm]: { display: "none" },
    },
  },
  
  containerTable: {
    position: 'relative',
    zIndex: 0, // Lower z-index than header
    overflow: 'auto', // Keep auto for table scrolling
    // Momentum scrolling for the horizontal swipe on iOS.
    WebkitOverflowScrolling: 'touch',
    // Take the remaining vertical space inside the card so the footer below
    // stays visible; the table body scrolls here instead of the whole page.
    flex: "1 1 auto",
    minHeight: 0,
    
    // Ensure the table wrapper doesn't clip dropdowns from above
    '& .MuiTableContainer-root': {
      overflow: 'auto',
    },
  },
  
  iconSort: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    cursor: "pointer",
  },
  
  componentHeader: {
    marginLeft: "10px",
    marginRight: "10px",
    position: 'relative',
    zIndex: 'inherit',
    [down.sm]: {
      // The wrapped header is full-width on a phone; side margins there just
      // misalign this control against everything above it.
      marginLeft: 0,
      marginRight: 0,
    },
  },
}));

export default useStyles;